/*
 * Copyright (C) 2018 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import {bool, func} from 'prop-types'
import React from 'react'

import {useScope as createI18nScope} from '@canvas/i18n'

import {Grid} from '@instructure/ui-grid'
import {View} from '@instructure/ui-view'

import {assignLocation} from '@canvas/util/globalUtils'
import {TeacherAssignmentShape} from '../assignmentData'
import AssignmentGroup from './Editables/AssignmentGroup'
import AssignmentModules from './Editables/AssignmentModules'
import AssignmentName from './Editables/AssignmentName'
import AssignmentType from './Editables/AssignmentType'
import TeacherViewContext from './TeacherViewContext'
import Toolbox from './Toolbox'

const I18n = createI18nScope('assignments_2')

// TODO: the assignment type and module selection need to be factored out into
// their own components. too much logic to cram in here

const confirmQuizType = I18n.t(
  'Quizzes are not yet handled in the new assignments flow. Head to the legacy create quiz page?',
)
const confirmPeerReviewType = I18n.t(
  'Peer reviewed assignments are not yet handled in the new assignments flow. Head to the legacy create assignment page?',
)
const confirmGroupType = I18n.t(
  'Creating a group is not yet handled by the new assignments flow. Head to the legacy assignments page?',
)

// @ts-expect-error
function assignmentIsNew(assignment) {
  return !assignment.lid
}

// TODO: if the Header is initially rendered with all items in edit mode
// because we're creating a new assignment, then the logic for getting the
// list of available modules and groups will not work

export default class Header extends React.Component {
  static contextType = TeacherViewContext

  static propTypes = {
    assignment: TeacherAssignmentShape.isRequired,
    onChangeAssignment: func.isRequired,
    onValidate: func.isRequired,
    invalidMessage: func.isRequired,
    onSetWorkstate: func.isRequired,
    onPublishChange: func,
    onDelete: func,
    readOnly: bool,
  }

  static defaultProps = {
    onPublishChange: () => {},
    onDelete: () => {},
    readOnly: false,
  }

  // @ts-expect-error
  constructor(props) {
    super(props)

    const isNewAssignment = assignmentIsNew(props.assignment)
    const initialMode = !props.readOnly && isNewAssignment ? 'edit' : 'view'
    this.state = {
      assignmentTypeMode: initialMode,
      selectedAssignmentType: isNewAssignment ? null : 'assignment',

      modulesMode: initialMode,

      assignmentGroupMode: initialMode,

      nameMode: initialMode,
    }
  }

  // @ts-expect-error
  handleTypeModeChange = mode => {
    this.setState({assignmentTypeMode: mode})
  }

  // @ts-expect-error
  handleTypeChange = selectedType => {
    switch (selectedType) {
      case 'assignment':
        break
      case 'quiz':
        if (window.confirm(confirmQuizType)) {
          // must be true, because that's the only way quiz is an option
          // @ts-expect-error
          if (ENV.QUIZ_LTI_ENABLED) {
            // @ts-expect-error
            assignLocation(`/courses/${this.props.assignment.course.lid}/assignments/new?quiz_lti`)
          }
        }
        break
      case 'peer-review':
        if (window.confirm(confirmPeerReviewType)) {
          // @ts-expect-error
          assignLocation(`/courses/${this.props.assignment.course.lid}/assignments/new`)
        }
        break
      case 'group':
        if (window.confirm(confirmGroupType)) {
          // @ts-expect-error
          assignLocation(`/courses/${this.props.assignment.course.lid}/assignments/new`)
        }
        break
    }
    // Awkward, but there's a delicate interaction between Header, AssignmentType,
    // and SelectableText so that we have to update our state to reflect the new
    // selectedType, even though it's an invalid value, then set it back
    // to assignment.
    // Without this, AssignmentType's props don't change, it won't rerender, so
    // it doesn't rerender SelectableText which still holds the value the user selected.
    // I was hoping there was a cleaner way of doing this, but I'm stumped.
    this.setState({selectedAssignmentType: selectedType}, () => {
      this.forceUpdate()
      this.setState({
        selectedAssignmentType: 'assignment', // can't change it yet
      })
    })
  }

  // @ts-expect-error
  handleModulesChange = selectedModules => {
    // @ts-expect-error
    this.props.onChangeAssignment('modules', selectedModules)
  }

  // @ts-expect-error
  handleModulesChangeMode = mode => {
    this.setState({modulesMode: mode})
  }

  // TODO: support +Module
  // handleAddModule = (newModule, currentSelection) => {
  //   // get the new module created
  //   const newlyCreatedModule = {...newModule, lid: '9999'}
  //   this.setState((prevState, _prevProps) => ({
  //     selectedModules: currentSelection.concat([newlyCreatedModule]),
  //     moduleList: prevState.moduleList.concat([newlyCreatedModule])
  //   }))
  // }

  // @ts-expect-error
  handleGroupChange = selectedAssignmentGroup => {
    // @ts-expect-error
    this.props.onChangeAssignment('assignmentGroup', selectedAssignmentGroup)
  }

  // @ts-expect-error
  handleGroupChangeMode = mode => {
    this.setState({assignmentGroupMode: mode})
  }

  // @ts-expect-error
  handleNameChange = name => {
    // @ts-expect-error
    this.props.onChangeAssignment('name', name)
  }

  // @ts-expect-error
  handleNameChangeMode = mode => {
    this.setState({nameMode: mode})
  }

  render() {
    // @ts-expect-error
    const assignment = this.props.assignment
    return (
      <Grid startAt="large" colSpacing="large">
        <Grid.Row>
          <Grid.Col>
            <View display="block" padding="small 0 medium xx-small">
              <AssignmentType
                // @ts-expect-error
                mode={this.state.assignmentTypeMode}
                // @ts-expect-error
                selectedAssignmentType={this.state.selectedAssignmentType}
                onChange={this.handleTypeChange}
                onChangeMode={this.handleTypeModeChange}
                // @ts-expect-error
                readOnly={this.props.readOnly}
              />
            </View>
            <View display="block" padding="xx-small 0 0 xx-small">
              <AssignmentModules
                // @ts-expect-error
                mode={this.state.modulesMode}
                courseId={assignment.course.lid}
                selectedModules={assignment.modules}
                onChange={this.handleModulesChange}
                onChangeMode={this.handleModulesChangeMode}
                // @ts-expect-error
                onAddModule={this.handleAddModule}
                // @ts-expect-error
                readOnly={this.props.readOnly}
              />
            </View>
            <View display="block" padding="xx-small 0 0 xx-small">
              <AssignmentGroup
                // @ts-expect-error
                mode={this.state.assignmentGroupMode}
                courseId={assignment.course.lid}
                selectedAssignmentGroup={assignment.assignmentGroup}
                onChange={this.handleGroupChange}
                onChangeMode={this.handleGroupChangeMode}
                // @ts-expect-error
                onAddGroup={this.handleAddGroup}
                // @ts-expect-error
                readOnly={this.props.readOnly}
              />
            </View>
            <View display="block" padding="medium xx-small large xx-small">
              <AssignmentName
                // @ts-expect-error
                mode={this.state.nameMode}
                name={assignment.name}
                onChange={this.handleNameChange}
                onChangeMode={this.handleNameChangeMode}
                // @ts-expect-error
                onValidate={this.props.onValidate}
                // @ts-expect-error
                invalidMessage={this.props.invalidMessage}
                // @ts-expect-error
                readOnly={this.props.readOnly}
              />
            </View>
          </Grid.Col>
          <Grid.Col width="auto" textAlign="end">
            {/* @ts-expect-error */}
            <Toolbox {...this.props} />
          </Grid.Col>
        </Grid.Row>
      </Grid>
    )
  }
}
